import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Zap, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Carousel2 from './Carousel2.jsx';
import DetallesProducto from './DetallesProducto.jsx';
import RecomendedProductList from './RecomendedProductList.jsx';
import { BrandButton } from '../../../components/ui/index.js';
import { useCartContext } from '../../../components/providers/CartProvider.jsx';

const fmt = (n) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCartContext();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [added, setAdded] = useState(false);

    useEffect(() => {
        axios.get(`/products/${id}/`)
            .then(r => setProduct(r.data))
            .catch(e => console.error('Error fetching product:', e))
            .finally(() => setLoading(false));
    }, [id]);

    const handleAddToCart = (e) => {
        e.stopPropagation();
        addToCart(product);
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <span className="loading loading-spinner loading-lg text-brand-red" />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
                <p className="text-lg">Producto no encontrado</p>
                <BrandButton variant="ghost" size="sm" onClick={() => navigate('/store')}>
                    <ArrowLeft size={14} /> Volver a la tienda
                </BrandButton>
            </div>
        );
    }

    return (
        <div className="h-full w-full overflow-y-scroll" style={{ minHeight: 0 }}>
            <div className="px-4 md:px-10 lg:px-20 py-6 flex flex-col gap-8">

                {/* Back */}
                <div>
                    <BrandButton variant="ghost" size="sm" onClick={() => navigate('/store')}>
                        <ArrowLeft size={14} /> Tienda
                    </BrandButton>
                </div>

                {/* ── Hero section ─────────────────────────────────────── */}
                <motion.section
                    className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                >
                    {/* Carousel */}
                    <div className="glassCard p-4 rounded-2xl h-[420px] lg:h-[520px]">
                        <Carousel2 product={product} />
                    </div>

                    {/* Info */}
                    <div className="glassCard p-6 rounded-2xl flex flex-col gap-4 lg:min-h-[520px]">
                        {/* Category */}
                        {product.category?.name && (
                            <span className="text-xs uppercase tracking-widest text-primary font-semibold">
                                {product.category.name}
                            </span>
                        )}

                        {/* Name */}
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight dark:text-white text-gray-900">
                            {product.name}
                        </h1>

                        {/* Price */}
                        <p className="text-3xl font-bold text-brand-red">
                            {fmt(product.price)}
                        </p>

                        {/* Short description */}
                        {product.short_description && (
                            <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                                {product.short_description}
                            </p>
                        )}

                        {/* Stock badge */}
                        {product.stock !== undefined && (
                            <span className={`self-start text-xs font-medium px-3 py-1 rounded-full
                                ${product.stock > 0
                                    ? 'bg-green-500/10 text-green-500'
                                    : 'bg-red-500/10 text-red-400'
                                }`}>
                                {product.stock > 0 ? `${product.stock} en stock` : 'Agotado'}
                            </span>
                        )}

                        {/* CTA */}
                        <div className="mt-auto pt-4 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                            <BrandButton
                                variant="solid"
                                size="md"
                                className="flex-1 justify-center"
                                onClick={handleAddToCart}
                                disabled={product.stock === 0}
                            >
                                <ShoppingCart size={16} />
                                {added ? '¡Añadido!' : 'Añadir al carrito'}
                            </BrandButton>
                            <BrandButton
                                variant="outline"
                                size="md"
                                className="flex-1 justify-center"
                                disabled={product.stock === 0}
                            >
                                <Zap size={16} />
                                Comprar ahora
                            </BrandButton>
                        </div>
                    </div>
                </motion.section>

                {/* ── Details accordion ────────────────────────────────── */}
                <motion.section
                    className="glassCard rounded-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    viewport={{ once: true, amount: 0.1 }}
                >
                    <DetallesProducto product={product} />
                </motion.section>

                {/* ── Recommended ──────────────────────────────────────── */}
                <motion.section
                    className="flex flex-col gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: 'easeOut' }}
                    viewport={{ once: true, amount: 0.1 }}
                >
                    <h2 className="text-2xl font-bold dark:text-white text-gray-900">
                        También te puede interesar
                    </h2>
                    <div className="glassCard p-4 rounded-2xl">
                        <RecomendedProductList />
                    </div>
                </motion.section>

            </div>
        </div>
    );
};

export default ProductDetail;
